import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Loading, Empty, NativeSelect, StatusBadge } from "@/components/common";
import { AuditTable } from "@/components/AuditTable";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { fmtDate, fmtDateTime, invNo, num, periodLabel } from "@/lib/format";

function useFetch(url, params) {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params || {});
  useEffect(() => {
    setD(null);
    api.get(url, { params: JSON.parse(key) }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [url, key]);
  return d;
}

function InvoiceHistory() {
  const { can } = useAuth();
  const rows = useFetch("/invoices");
  if (!rows) return <Loading />;
  if (!rows.length) return <Empty>Belum ada invoice.</Empty>;
  const byPeriod = rows.reduce((m, r) => ({ ...m, [r.period]: [...(m[r.period] || []), r] }), {});
  return (
    <div className="space-y-3">
      {can("export:run") && <Button variant="outline" size="sm" onClick={() => downloadCsv("/export/invoices", "invoice-semua")} data-testid="history-export-invoices"><Download className="h-4 w-4 mr-2" />Export semua invoice</Button>}
      <div className="xl-wrap max-h-[60vh]">
        <table className="xl" data-testid="history-invoice-table">
          <thead><tr><th className="rownum">#</th><th>Periode</th><th className="text-right">Invoice</th><th className="text-right">Disahkan</th><th className="text-right">Total Qty</th><th className="text-right">Total (Rp)</th></tr></thead>
          <tbody>
            {Object.entries(byPeriod).sort((a, b) => b[0].localeCompare(a[0])).map(([p, list], i) => (
              <tr key={p}><td className="rownum">{i + 1}</td><td>{periodLabel(p)}</td><td className="num">{list.length}</td><td className="num">{list.filter((x) => x.status === "approved").length}</td>
                <td className="num">{num(list.filter((x) => x.status !== "cancelled").reduce((s, x) => s + x.total_qty, 0))}</td><td className="num">{num(list.filter((x) => x.status !== "cancelled").reduce((s, x) => s + x.total, 0))}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="xl-wrap max-h-[50vh]">
        <table className="xl">
          <thead><tr><th className="rownum">#</th><th>No. Nota</th><th>Tanggal</th><th>Pelanggan</th><th className="text-right">Total (Rp)</th><th>Status</th><th>Disahkan oleh</th><th className="text-right">Cetak</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id}><td className="rownum">{i + 1}</td><td className="font-mono">{invNo(r)}</td><td className="text-xs">{fmtDate(r.date)}</td><td>{r.customer_name}</td><td className="num">{num(r.total)}</td><td><StatusBadge status={r.status} />{r.status === "cancelled" && <div className="text-[10px] text-slate-500 max-w-[220px] truncate" title={r.cancel_reason}>oleh {r.cancelled_by}: {r.cancel_reason}</div>}</td><td className="text-xs">{r.approved_by || "-"}</td><td className="num">{r.print_count}x</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function PrintHistory() {
  const rows = useFetch("/prints");
  if (!rows) return <Loading />;
  if (!rows.length) return <Empty>Belum ada riwayat pencetakan.</Empty>;
  return (
    <div className="xl-wrap max-h-[65vh]">
      <table className="xl" data-testid="history-print-table">
        <thead><tr><th className="rownum">#</th><th>Waktu</th><th>Oleh</th><th className="text-right">Nota</th><th className="text-right">Per Hal.</th><th className="text-right">Halaman</th><th>Jenis</th><th>Nomor Nota</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.id}><td className="rownum">{i + 1}</td><td className="font-mono text-xs">{fmtDateTime(r.created_at)}</td><td className="font-mono text-xs">{r.printed_by}</td><td className="num">{r.count}</td><td className="num">{r.notas_per_page}</td><td className="num">{r.pages}</td>
            <td>{r.reprint ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800">CETAK ULANG</span> : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">BARU</span>}</td>
            <td className="text-xs text-slate-600 max-w-[360px] truncate" title={r.numbers.join(", ")}>{r.numbers.join(", ")}{r.replaced_numbers?.length > 0 && <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-rose-100 text-rose-800" title={r.replaced_numbers.join(", ")} data-testid={`history-print-replaced-${i}`}>DIGANTI: {r.replaced_numbers.join(", ")}</span>}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

const AUDIT_FILTERS = [["", "Semua aktivitas"], ["login", "Login"], ["harga", "Perubahan harga"], ["invoice", "Invoice"], ["cetak", "Pencetakan"], ["pelanggan", "Pelanggan"], ["rekap", "Rekap"], ["pengguna", "Pengguna"]];

function AuditHistory() {
  const [action, setAction] = useState("");
  const rows = useFetch("/audit", { action });
  return (
    <div className="space-y-3">
      <NativeSelect value={action} onChange={(e) => setAction(e.target.value)} data-testid="history-audit-filter">
        {AUDIT_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </NativeSelect>
      {!rows ? <Loading /> : <AuditTable rows={rows} />}
    </div>
  );
}

export default function HistoryPage() {
  const { can } = useAuth();
  return (
    <div>
      <PageHeader sheet="SHEET 08" title="Riwayat dan Laporan" subtitle="Riwayat invoice, pencetakan, dan audit log perusahaan Anda." />
      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-history-invoices">Invoice & Laporan</TabsTrigger>
          <TabsTrigger value="prints" data-testid="tab-history-prints">Pencetakan</TabsTrigger>
          {can("audit:read") && <TabsTrigger value="audit" data-testid="tab-history-audit">Audit Log</TabsTrigger>}
        </TabsList>
        <TabsContent value="invoices" className="mt-4"><InvoiceHistory /></TabsContent>
        <TabsContent value="prints" className="mt-4"><PrintHistory /></TabsContent>
        {can("audit:read") && <TabsContent value="audit" className="mt-4"><AuditHistory /></TabsContent>}
      </Tabs>
    </div>
  );
}
